/* Mail en AI (/crm/mail-en-ai, alleen de super_admin; ADR 0029): wat NATS met de mail van het
 * Greenvision-postvak doet en wat het taalmodel daarvoor kost.
 *
 *   kop        wanneer NATS voor het laatst aanleverde
 *   tegels     aanlevering, kosten deze maand, kosten vandaag, nog te beoordelen
 *   links      per dag (14 dagen), per functie (deze maand)
 *   rechts     de stand per postvak
 *   onderaan   hoe het bedrag is berekend
 *
 * Het CRM leest zelf geen mail: NATS stuurt tellingen (migratie 106). Het bedrag is tokens maal het
 * tarief in data/tarieven.ts, een schatting dus. */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarDays, Coins, Hourglass, RadioTower } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useOpvolgTelling } from '@/features/opvolging/hooks';
import { useMailAi } from './hooks';
import {
  dagTerug, eersteVanMaand, FUNCTIE_LABEL, geleden, lokaleDag, overzicht, perDag, perFunctie, tussen, VERS_MINUTEN,
  type MailAi, type Som, type Stand,
} from './data/mailAi';
import { TARIEF_BRON, TARIEVEN } from './data/tarieven';

const GETAL = new Intl.NumberFormat('nl-NL');
const USD = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const USD_TARIEF = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 3 });
const DAG = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
const DAG_LANG = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
const TIJDSTIP = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const MAAND = new Intl.DateTimeFormat('nl-NL', { month: 'long' });

const getal = (n: number) => GETAL.format(n);
const middag = (dag: string) => new Date(`${dag}T12:00:00`);
const tijdstip = (iso: string | null) => (iso ? TIJDSTIP.format(new Date(iso.replace(' ', 'T'))) : 'nog niet');
/** Een bedrag onder de cent is niet nul. */
const bedrag = (usd: number) => (usd > 0 && usd < 0.005 ? `< ${USD.format(0.01)}` : USD.format(usd));
const kosten = (s: Som) => (s.aanroepen > 0 && s.zonderTarief === s.aanroepen ? 'tarief onbekend' : bedrag(s.usd));

function Tegel({ icoon: Icoon, label, waarde, onder, dringend }: { icoon: typeof Coins; label: string; waarde: string; onder?: string | null; dringend?: boolean }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1 rounded-xl border bg-background p-3.5', dringend && 'border-amber-300 bg-amber-50/60 dark:bg-amber-950/20')}>
      <span className="flex items-center gap-1.5 text-xs text-foreground/80">
        <Icoon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </span>
      <span className="text-xl font-semibold tabular-nums sm:text-2xl">{waarde}</span>
      {onder && <span className="text-xs text-muted-foreground">{onder}</span>}
    </div>
  );
}

function Blok({ titel, id, rechts, children }: { titel: string; id: string; rechts?: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={id} className="text-lg font-semibold">{titel}</h2>
        {rechts && <span className="text-sm text-muted-foreground">{rechts}</span>}
      </div>
      {children}
    </section>
  );
}

const TH = 'px-3 py-2 text-right text-xs font-medium text-muted-foreground first:text-left';
const TD = 'px-3 py-2 text-right tabular-nums first:text-left';
/** Een nul leest als "niets": grijs, zodat de dagen waarop wel iets gebeurde opvallen. */
const Cel = ({ n, klasse }: { n: number; klasse?: string }) => <td className={cn(TD, n === 0 ? 'text-muted-foreground/60' : klasse)}>{getal(n)}</td>;

function PerDag({ m }: { m: MailAi }) {
  const regels = perDag(m);
  return (
    <Blok titel="Per dag" id="mailai-dagen" rechts="laatste 14 dagen">
      <div className="overflow-x-auto rounded-xl border bg-background">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b">
              <th className={TH}>Dag</th>
              <th className={TH}>Mail in</th>
              <th className={TH}>Mail uit</th>
              <th className={TH}>Aanroepen</th>
              <th className={TH}>Tokens in</th>
              <th className={TH}>Tokens uit</th>
              <th className={TH}>Mislukt</th>
              <th className={TH}>Kosten</th>
            </tr>
          </thead>
          <tbody>
            {regels.map((r) => {
              return (
                <tr key={r.dag} className="border-b last:border-0">
                  <td className={TD}>{DAG.format(middag(r.dag))}</td>
                  <Cel n={r.mail_in} />
                  <Cel n={r.mail_uit} />
                  <Cel n={r.aanroepen} />
                  <Cel n={r.input_tokens} />
                  <Cel n={r.output_tokens} />
                  <Cel n={r.fouten} klasse="font-medium text-amber-700 dark:text-amber-400" />
                  <td className={cn(TD, r.aanroepen ? 'font-medium' : 'text-muted-foreground/60')}>{r.aanroepen ? kosten(r) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Blok>
  );
}

function PerFunctie({ m, nu }: { m: MailAi; nu: Date }) {
  const vandaag = lokaleDag(nu);
  const regels = perFunctie(tussen(m.ai, eersteVanMaand(vandaag), vandaag));
  return (
    <Blok titel="Per functie" id="mailai-functies" rechts={MAAND.format(nu)}>
      <div className="overflow-x-auto rounded-xl border bg-background">
        {regels.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Deze maand is het taalmodel nog niet aangeroepen.</p>
        ) : (
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b">
                <th className={TH}>Functie</th>
                <th className={TH}>Aanroepen</th>
                <th className={TH}>Tokens in</th>
                <th className={TH}>Tokens uit</th>
                <th className={TH}>Mislukt</th>
                <th className={TH}>Kosten</th>
              </tr>
            </thead>
            <tbody>
              {regels.map((r) => (
                <tr key={`${r.functie}|${r.model}`} className="border-b last:border-0">
                  <td className={TD}>
                    <span className="font-medium">{FUNCTIE_LABEL[r.functie] ?? r.functie}</span>
                    <span className="block text-xs text-muted-foreground">{r.model}</span>
                  </td>
                  <Cel n={r.aanroepen} />
                  <Cel n={r.input_tokens} />
                  <Cel n={r.output_tokens} />
                  <Cel n={r.fouten} klasse="font-medium text-amber-700 dark:text-amber-400" />
                  <td className={cn(TD, 'font-medium')}>{kosten(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Blok>
  );
}

function Regel({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b py-2 text-sm last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right tabular-nums">{children}</dd>
    </div>
  );
}

function StandBlok({ s, nu, opvolg }: { s: Stand; nu: Date; opvolg: { open: number; teLaat: number } | undefined }) {
  const punten = s.punten_toezegging + s.punten_wacht + s.punten_vraag;
  return (
    <Blok titel="Stand van de mail" id={`mailai-stand-${s.postvak}`} rechts={s.postvak}>
      <dl className="rounded-xl border bg-background px-4 py-1">
        <Regel label="Laatste mail in NATS">{tijdstip(s.laatste_mail_op)}</Regel>
        <Regel label="Laatste oordeel">{s.laatste_oordeel_op ? geleden(s.laatste_oordeel_op, nu) : 'nog niet'}</Regel>
        <Regel label="Draden beoordeeld">
          {getal(s.draden_beoordeeld + s.draden_ruis)}
          <span className="text-muted-foreground"> · waarvan {getal(s.draden_ruis)} ruis</span>
        </Regel>
        <Regel label="Nog te beoordelen">{getal(s.draden_wachtrij)}</Regel>
        <Regel label="Oordeel mislukt">
          <span className={cn(s.draden_fout > 0 && 'font-medium text-amber-700 dark:text-amber-400')}>{getal(s.draden_fout)}</span>
        </Regel>
        <Regel label="Wie is aan zet">
          ik {getal(s.aan_zet_ik)} <span className="text-muted-foreground">·</span> de ander {getal(s.aan_zet_zij)}
        </Regel>
        <Regel label="Open volgens het draadoordeel">
          {getal(punten)}
          <span className="block text-xs text-muted-foreground">
            {getal(s.punten_toezegging)} toegezegd · {getal(s.punten_wacht)} wacht op antwoord · {getal(s.punten_vraag)} vraag aan ons
            {s.punten_te_laat > 0 && ` · ${getal(s.punten_te_laat)} te laat`}
          </span>
        </Regel>
        <Regel label="Opvolgpunten in het CRM">
          <Link to="/crm" className="underline-offset-2 hover:underline">
            {opvolg ? `${getal(opvolg.open)} open${opvolg.teLaat ? ` · ${getal(opvolg.teLaat)} te laat` : ''}` : '·'}
          </Link>
        </Regel>
      </dl>
      <p className="text-xs text-muted-foreground">
        Het draadoordeel draait nog in schaduw: wat het vindt staat in NATS en komt pas in het CRM als je het aanzet. De opvolgpunten in
        het CRM komen nu nog uit de oude toezeggingenscan.
      </p>
    </Blok>
  );
}

function Verantwoording() {
  const perModel = new Map<string, typeof TARIEVEN>();
  for (const t of TARIEVEN) perModel.set(t.model, [...(perModel.get(t.model) ?? []), t]);
  return (
    <footer className="flex flex-col gap-1 border-t pt-4 text-xs text-muted-foreground">
      <p>
        Kosten zijn een schatting: het aantal tokens maal het tarief van het model. De rekening zelf staat bij Google en loopt in dollars.
        Hier staan alleen de twee functies die hun tokens vastleggen, en alleen voor de mail van het Greenvision-postvak; andere onderdelen
        van NATS die dezelfde sleutel gebruiken staan er niet in.
      </p>
      {[...perModel.entries()].map(([model, lijst]) => (
        <p key={model}>
          {model}:{' '}
          {lijst
            .map((t) => `${USD_TARIEF.format(t.inPerMiljoen)} in en ${USD_TARIEF.format(t.uitPerMiljoen)} uit per miljoen tokens${t.vanaf ? ` vanaf ${DAG_LANG.format(middag(t.vanaf))}` : ''}`)
            .join('; ')}
          . Bron:{' '}
          <a href={TARIEF_BRON.url} target="_blank" rel="noreferrer" className="underline underline-offset-2">{TARIEF_BRON.naam}</a>, overgenomen op{' '}
          {DAG_LANG.format(middag(TARIEF_BRON.gezien))}.
        </p>
      ))}
    </footer>
  );
}

export function MailAiPagina() {
  const { user, hasRole } = useAuth();
  const mag = hasRole('super_admin');
  const mailAi = useMailAi(mag);
  const opvolg = useOpvolgTelling(mag ? user?.id : undefined);
  const nu = new Date();

  if (!mag) {
    return <p className="rounded-xl border bg-background p-6 text-sm text-muted-foreground">Deze pagina is alleen voor de beheerder.</p>;
  }

  const kop = (
    <header className="flex flex-col">
      <h1 className="text-2xl font-semibold tracking-tight">Mail en AI</h1>
      <p className="text-sm text-muted-foreground">Wat NATS met de mail van het Greenvision-postvak doet, en wat het taalmodel daarvoor kost.</p>
    </header>
  );

  if (mailAi.isError) {
    return (
      <div className="flex flex-col gap-5">
        {kop}
        <p className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50/60 p-4 text-sm dark:bg-amber-950/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {(mailAi.error as Error)?.message ?? 'De gegevens konden niet worden gelezen.'}
        </p>
      </div>
    );
  }
  if (!mailAi.data) {
    return (
      <div className="flex flex-col gap-5">
        {kop}
        <div className="h-24 animate-pulse rounded-xl bg-muted" />
        <div className="h-64 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  const m = mailAi.data;
  if (m.standen.length === 0) {
    return (
      <div className="flex flex-col gap-5">
        {kop}
        <p className="rounded-xl border bg-background p-6 text-sm text-muted-foreground">
          NATS heeft nog niets aangeleverd. De eerste telling komt met de volgende aanlevering van de opvolgpunten, binnen tien minuten.
        </p>
        <Verantwoording />
      </div>
    );
  }

  const o = overzicht(m, nu);
  const laatsteMail = m.standen.map((s) => s.laatste_mail_op).filter(Boolean).sort().pop() ?? null;
  const vandaag = lokaleDag(nu);
  const mailWeek = tussen(m.dagen, dagTerug(vandaag, 6), vandaag);

  return (
    <div className="flex flex-col gap-5">
      {kop}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Tegel icoon={RadioTower} label="Aanlevering uit NATS" waarde={geleden(o.ontvangenOp, nu)} dringend={!o.vers}
          onder={o.vers ? `laatste mail ${tijdstip(laatsteMail)}` : `ouder dan ${VERS_MINUTEN} minuten: NATS levert niet aan`} />
        <Tegel icoon={Coins} label={`Kosten ${MAAND.format(nu)}`} waarde={kosten(o.dezeMaand)}
          onder={`${getal(o.dezeMaand.aanroepen)} aanroepen · vorige maand ${o.vorigeMaand.aanroepen ? kosten(o.vorigeMaand) : 'geen'}`} />
        <Tegel icoon={CalendarDays} label="Kosten vandaag" waarde={kosten(o.vandaag)}
          onder={`${getal(o.vandaag.aanroepen)} aanroepen · ${getal(mailWeek.reduce((t, d) => t + d.mail_in + d.mail_uit, 0))} mails in 7 dagen`} />
        <Tegel icoon={Hourglass} label="Nog te beoordelen" waarde={getal(o.wachtrij)} dringend={o.foutenWeek > 0}
          onder={o.foutenWeek ? `${getal(o.foutenWeek)} mislukt in 7 dagen` : 'niets mislukt in 7 dagen'} />
      </div>

      {(o.dezeMaand.zonderTarief > 0 || o.vorigeMaand.zonderTarief > 0) && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/20">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          Er zijn aanroepen van een model zonder tarief in dit overzicht; die tellen niet mee in het bedrag.
        </p>
      )}

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
        <div className="flex min-w-0 flex-col gap-6">
          <PerDag m={m} />
          <PerFunctie m={m} nu={nu} />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          {m.standen.map((s) => <StandBlok key={s.postvak} s={s} nu={nu} opvolg={opvolg.data} />)}
        </div>
      </div>

      <Verantwoording />
    </div>
  );
}
